/**
 * Game launch URL helpers.
 *
 * The game client starts in whatever language its own localStorage happens to
 * hold, which is not necessarily the language the player is reading the portal
 * in. Carrying the portal's language across the handoff keeps the two halves of
 * the product consistent for a student who has just picked a language here.
 */

import i18n from '../i18n/config';

/** Query parameter the game client reads for its startup UI language. */
const UI_LANGUAGE_PARAM = 'lang';

/**
 * The portal's active UI language as a bare code (e.g. "pt-BR" -> "pt").
 */
export function currentUiLanguage(): string {
  return (i18n.language || 'en').toLowerCase().split('-')[0];
}

/**
 * Append the portal's UI language to a game launch URL — every way into the
 * game: a student's session, a campaign's, an admin's launch, a moderator's
 * join.
 *
 * The language is each person's own: a teammate may read the game in another
 * language than the rest of the team. The game client also accepts the
 * session's transcription `language`, and prefers this one.
 *
 * Returns the URL unchanged if it cannot be parsed, so a launch never breaks
 * on account of a language hint.
 */
export function withUiLanguage(launchUrl: string): string {
  try {
    const url = new URL(launchUrl);
    url.searchParams.set(UI_LANGUAGE_PARAM, currentUiLanguage());
    return url.toString();
  } catch {
    return launchUrl;
  }
}

/** What the game's moderator join reads from its link, besides its mode and language. */
interface ModeratorJoin {
  /** The moderator's Nakama custom id. */
  user: string;
  name: string;
  cohort: string;
  world: string;
  match: string;
}

/**
 * The game's join link for a moderator, which the moderation page opens in a
 * new tab: who is joining, the cohort, world and match they moderate, and the
 * language they read the portal in.
 */
export function moderatorJoinUrl(gameClientUrl: string, join: ModeratorJoin): string {
  const url = new URL(gameClientUrl);
  url.searchParams.set('mode', 'moderator-join');
  url.searchParams.set('user', join.user);
  url.searchParams.set('name', join.name);
  url.searchParams.set('cohort', join.cohort);
  url.searchParams.set('world', join.world);
  url.searchParams.set('match', join.match);
  return withUiLanguage(url.toString());
}
